import lead from 'api/lead';
import {
  BULK_LEAD_CREATION,
  CHECK_LEAD_CREATION,
  CREATE_LEAD_B2B,
  CREATE_LEAD_B2C,
  DOWNLOAD_CSV,
  DOWNLOAD_LEAD_LIST,
  DOWNLOAD_LEAD_SAMPLE,
  FETCH_CSV_HISTORY,
  GET_LEAD_LIST,
  GET_ROW_ID_STATUS,
  STOP_FILE_HISTORY_POLLING,
  UPDATE_LEAD_STATUS
} from 'redux/sagas/actions/lead';
import {
  HIDE_SPINNER,
  SET_SHOW_HISTORY_TAB,
  SHOW_SPINNER
} from 'redux/slices/lead';
import {
  call,
  cancelled,
  delay,
  put,
  race,
  select,
  take,
  takeLatest
} from 'redux-saga/effects';
import moment from 'moment';
import Toast from '../../ui/components/Toast';
import i18next from 'i18next';
import { TOGGLE_SIDEBAR } from '../slices/sidebar';
import {
  downloadCSVFIle,
  transformCSVHistoryData,
  transformHistoryRowData
} from '../../api/helpers/people';
import { FILE_UPLOAD_COMPLETE, FILE_UPLOAD_START } from '../slices/people';
import { MODAL_CLOSE } from '../slices/modals';
import { SIGNOUT } from './actions/authentication';

const getPeriodOrDefault = filters => {
  if (Object.keys(filters).length > 0) {
    return filters.period;
  }

  return [`${moment().add(-3, 'month')}`, `${moment()}`];
};

const formatDataForLeadCreation = (data, recordType, leadType) => {
  return {
    Building__c: data.building,
    Car_Brand__c: data.carBrand?.value,
    City: data.city,
    Company: data.companyName,
    Company_Legal_Name__c: data.companyName,
    Company_ValueAddedTax__c: data.vatNumber,
    Country: '',
    // CreatedDate: `${moment().format('YYYY-MM-DDTHH:mm:ss')}Z`,
    Email: data.email,
    FirstName: data.firstName,
    Fiscal_Code__c: data.fiscalCode,
    //LastModifiedDate: `${moment().format('YYYY-MM-DDTHH:mm:ss')}Z`,
    LastName: data.lastName,
    //LeadSource: leadType,
    MobilePhone: data.phoneNumber2,
    Name_of_the_Dealer__c: data.dealerName,
    Note__c: data.notes,
    Phone: data.phoneNumber,
    PostalCode: data.zip,
    Prefix_Secondary_Phone__c: data.prefix2?.split(' ')?.[0],
    Prefix__c: data.prefix?.split(' ')?.[0],
    Product_of_Interest__c:
      data?.productInterest?.map(p => p.value).join(';') || '',
    RecordType: { Name: leadType },
    //State: data.state,
    //Status: data.status,
    Street: data.street,
    Type_of_Vehicle__c: data.typeOfVehicle,
    Vehicle_Delivery__c: data.vehicleDelivery
      ? `${moment(data.vehicleDelivery, 'DD/MM/YY').format(
          'YYYY-MM-DDTHH:mm:ss.S'
        )}Z`
      : null,
    Vehicle_Identification_Number__c: data.vin,
    Marketing__c: 'on'
  };
};

function* getLeadList({ payload }) {
  try {
    yield put(SHOW_SPINNER());
    const {
      selectedTypes,
      selectedStatuses,
      selectedCountries,
      sortDirection,
      sortBy,
      name,
      surname,
      email
    } = yield select(state => state.lead);

    const { data } = yield call(lead.getLeadsApi, {
      pageNo: payload.pageNo,
      pageSize: payload.pageSize,
      type: selectedTypes?.length !== 0 ? selectedTypes : null,
      status: selectedStatuses?.length !== 0 ? selectedStatuses : null,
      dealerCountry: selectedCountries?.length !== 0 ? selectedCountries : null,
      sortBy: sortBy === 'fullName' ? 'LastName' : sortBy,
      sortDirection,
      email: email,
      name: name,
      surname: surname
    });

    const newData = {
      ...data,
      leads: data.leads.map(i => {
        console.log('i', i);
        return ({
        ...i,
        Status:
          i.Status === 'Technical Call'
            ? 'TECHNICAL_CALL'
            : i.Status === 'Commercial Call'
            ? 'COMMERCIAL_CALL'
            : i.Status === 'Quotation'
            ? 'QUOTATION' 
            : i.Status === 'Convertible'
            ? 'CONVERTIBLE'
            : i.Status === 'Closed'
            ? 'CLOSED'
            : i.Status
      })})
    };

    data.pageNo = payload.pageNo;
    data.pageSize = payload.pageSize;
    yield put({
      type: GET_LEAD_LIST._SUCCESS,
      payload: newData
    });
  } catch (err) {
    console.log('err: ', err);
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: GET_LEAD_LIST._ERROR });
  } finally {
    yield put(HIDE_SPINNER());
  }
}

function* getLeadListWatch() {
  yield takeLatest(GET_LEAD_LIST._REQUEST, getLeadList);
}

function* createLeadB2B({ payload }) {
  try {
    const { data } = yield call(lead.checkLead, {
      companyLegalName: payload.companyLegalName,
      email: payload.email
    });
    yield put({ type: CHECK_LEAD_CREATION._SUCCESS, payload: data });
    if (!data?.exists) {
      const BUSINESS_ID = yield select(store => store.app.config.BUSINESS_ID);
      const formattedData = formatDataForLeadCreation(
        payload.state,
        BUSINESS_ID,
        'Business'
      );
      yield call(lead.createLead, formattedData);
      yield put({ type: CREATE_LEAD_B2B._SUCCESS });
      yield put(TOGGLE_SIDEBAR());
      Toast.show(Toast.TYPE.SUCCESS, i18next.t('lead_creation_toast'));
    }
  } catch (err) {
    console.log({ err });
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    Toast.show(Toast.TYPE.ERROR, i18next.t('lead_creation_toast_error'));
    yield put({ type: CREATE_LEAD_B2B._ERROR });
  }
}

function* createLeadB2BWatch() {
  yield takeLatest(CREATE_LEAD_B2B._REQUEST, createLeadB2B);
}

function* createLeadB2C({ payload }) {
  try {
    const { data } = yield call(lead.checkLead, {
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email
    });
    yield put({ type: CHECK_LEAD_CREATION._SUCCESS, payload: data });
    if (!data?.exists) {
      const PERSON_ID = yield select(store => store.app.config.PERSON_ID);
      const formattedData = formatDataForLeadCreation(
        payload.state,
        PERSON_ID,
        'Person'
      );
      yield call(lead.createLead, formattedData);
      yield put({ type: CREATE_LEAD_B2C._SUCCESS });
      yield put(TOGGLE_SIDEBAR());
      Toast.show(Toast.TYPE.SUCCESS, i18next.t('lead_creation_toast'));
    }
  } catch (err) {
    console.log({ err });
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: CREATE_LEAD_B2C._ERROR });
    Toast.show(Toast.TYPE.ERROR, i18next.t('lead_creation_toast_error'));
  }
}

function* createLeadB2CWatch() {
  yield takeLatest(CREATE_LEAD_B2C._REQUEST, createLeadB2C);
}

function* checkIfLeadAlreadyExists({ payload }) {
  try {
    const { data } = yield call(lead.checkLead, payload);
    yield put({ type: CHECK_LEAD_CREATION._SUCCESS, payload: data });
  } catch (err) {
    console.log({ err });
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: CHECK_LEAD_CREATION._ERROR });
  }
}

function* checkIfLeadAlreadyExistsWatch() {
  yield takeLatest(CHECK_LEAD_CREATION._REQUEST, checkIfLeadAlreadyExists);
}

function* updateLeadStatus({ payload }) {
  try {
    const { data } = yield call(lead.updateLeadStatus, {
      ...payload
    });
    yield put({ type: UPDATE_LEAD_STATUS._SUCCESS });
    // eslint-disable-next-line no-unused-expressions
    data?.forEach(d => {
      if (!d.updateSuccessful) {
        Toast.show(
          Toast.TYPE.ERROR,
          i18next.t('lead_update_toast_error', { leadId: d.leadId })
        );
      } else {
        Toast.show(
          Toast.TYPE.SUCCESS,
          i18next.t('lead_update_toast', { leadId: d.leadId })
        );
      }
    });
    yield put({
      type: GET_LEAD_LIST._REQUEST,
      payload: {
        pageNo: 0,
        pageSize: 10
      }
    });
  } catch (err) {
    console.log({ err });
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: UPDATE_LEAD_STATUS._ERROR });
    // eslint-disable-next-line no-unused-expressions
    err?.data?.forEach(d => {
      Toast.show(
        Toast.TYPE.ERROR,
        i18next.t('lead_update_toast_error', { leadId: d.leadId })
      );
    });
  }
}

function* updateLeadStatusWatch() {
  yield takeLatest(UPDATE_LEAD_STATUS._REQUEST, updateLeadStatus);
}

function* downloadLeadSample({ payload }) {
  try {
    const { data } = yield call(lead.downloadLeadSample, payload);
    if (data) {
      downloadCSVFIle(data);
    }
    Toast.show(
      Toast.TYPE.SUCCESS,
      i18next.t('file_downloaded_toast_success_message')
    );
  } catch (err) {
    console.log({ err });
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: DOWNLOAD_LEAD_SAMPLE._ERROR });
    Toast.show(
      Toast.TYPE.ERROR,
      i18next.t('file_downloaded_toast_error_message')
    );
  }
}

function* downloadLeadSampleWatch() {
  yield takeLatest(DOWNLOAD_LEAD_SAMPLE._REQUEST, downloadLeadSample);
}

function* leadBulkCreation({ payload }) {
  try {
    yield put(FILE_UPLOAD_START());
    const { data } = yield call(lead.bulkLeadCreation, payload);
    yield put({ type: BULK_LEAD_CREATION._SUCCESS, payload: data });
    Toast.show(Toast.TYPE.SUCCESS, i18next.t('create_lead_bulk_success'));
    //yield put(TOGGLE_SIDEBAR());
    yield put(SET_SHOW_HISTORY_TAB(true));
  } catch (err) {
    console.log({ err });
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: BULK_LEAD_CREATION._ERROR });
    const errorString =
      err.response?.data?.message || i18next.t('create_lead_bulk_error');
    Toast.show(Toast.TYPE.ERROR, errorString);
  } finally {
    yield put(FILE_UPLOAD_COMPLETE());
  }
}

function* leadBulkCreationWatch() {
  yield takeLatest(BULK_LEAD_CREATION._REQUEST, leadBulkCreation);
}

function* downloadCSVFile({ payload }) {
  try {
    const resp = yield call(lead.downloadCSVApiCall, {
      ...payload
    });

    if (resp.data) {
      downloadCSVFIle(resp.data, payload.fileName);
    }

    yield put({ type: DOWNLOAD_CSV._SUCCESS, payload });
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: DOWNLOAD_CSV._ERROR });
  }
}

function* downloadCSVFileWatch() {
  yield takeLatest(DOWNLOAD_CSV._REQUEST, downloadCSVFile);
}

function* fetchCSVHistory() {
  const { pageNo, pageSize } = yield select(state => ({
    pageNo: state?.lead.pageNo,
    pageSize: state?.lead.pageSize
  }));

  let stopPolling = false;
  let countMaxPolling = 0;
  try {
    while (!stopPolling) {
      const { data } = yield call(lead.getCSVHistory);

      const transformedData = transformCSVHistoryData(data);

      yield put({ type: FETCH_CSV_HISTORY._SUCCESS, payload: transformedData });

      const inProgressList = data?.filter(
        row => row.fileStatus === 'INPROGRESS'
      );

      if (inProgressList.length === 0) {
        stopPolling = true;
        yield call(getLeadList, { payload: { pageNo, pageSize } });
      }
      countMaxPolling += 1;
      if (countMaxPolling === 5) {
        stopPolling = true;
      }

      yield delay(5000);
    }
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: FETCH_CSV_HISTORY._ERROR });
  } finally {
    if (yield cancelled()) {
      // eslint-disable-next-line no-console
      console.log('sagas > people > history', 'History Polling Cancelled');
    }
  }
}

function* fetchCSVHistoryWatch() {
  while (true) {
    const action = yield take(FETCH_CSV_HISTORY._REQUEST);

    yield race({
      task: call(fetchCSVHistory, action),
      cancel: take(STOP_FILE_HISTORY_POLLING._REQUEST)
    });
  }
}

function* getRowIDStatus({ payload }) {
  try {
    const response = yield call(lead.getRowIdResponse, {
      ...payload
    });

    const transformedData = transformHistoryRowData(response.data);

    yield put({ type: GET_ROW_ID_STATUS._SUCCESS, payload: transformedData });
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: GET_ROW_ID_STATUS._ERROR });
  }
}

function* getRowIDStatusWatch() {
  yield takeLatest(GET_ROW_ID_STATUS._REQUEST, getRowIDStatus);
}

function* downloadReportLeadPage({ payload }) {
  try {
    const period = getPeriodOrDefault(payload);

    const selectedEndDate = moment(period[1]);
    selectedEndDate.set('hour', 23);
    selectedEndDate.set('minute', 59);
    selectedEndDate.set('seconds', 59);
    const { data } = yield call(lead.downloadLeadFile, {
      startDate: `${moment(period?.[0]).format('YYYY-MM-DDTHH:mm:ss')}Z`,
      endDate: `${selectedEndDate.format('YYYY-MM-DDTHH:mm:ss')}Z`
    });
    if (data) {
      downloadCSVFIle(data, 'Lead data.csv');
    }
    yield put({ type: DOWNLOAD_LEAD_LIST._SUCCESS });
    yield put(MODAL_CLOSE());
    Toast.show(
      Toast.TYPE.SUCCESS,
      i18next.t('file_downloaded_toast_success_message')
    );
  } catch (err) {
    console.log(err);
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    Toast.show(
      Toast.TYPE.ERROR,
      i18next.t('file_downloaded_toast_error_message')
    );
    yield put(MODAL_CLOSE());
    yield put({ type: DOWNLOAD_LEAD_LIST._ERROR });
  }
}

function* downloadReportLeadPageWatcher() {
  yield takeLatest(DOWNLOAD_LEAD_LIST._REQUEST, downloadReportLeadPage);
}

export default [
  getLeadListWatch(),
  createLeadB2BWatch(),
  checkIfLeadAlreadyExistsWatch(),
  createLeadB2CWatch(),
  updateLeadStatusWatch(),
  downloadReportLeadPageWatcher(),
  downloadLeadSampleWatch(),
  leadBulkCreationWatch(),
  downloadCSVFileWatch(),
  getRowIDStatusWatch(),
  fetchCSVHistoryWatch()
];
